import type { PostgrestClient } from "@supabase/postgrest-js";
import type { Database, DatabaseWithOptions } from "../../../__definitions__/database-definitions";
import { useSupabaseLoader } from "../../../server/supabase/hooks";
import type { UseSupabaseLoaderState } from "../../../server/supabase/hooks/types";
import type { UUID } from "../../../utils/uuid";
import type { ChannelName } from "../../channels/types";
import { SupabaseAccountService } from "../../generic/server/supabase-account-service";
import { SupabaseMergeTagService } from "../../merge-tag/server/supabase-merge-tag-service";
import type { Tags } from "../../merge-tag/types";
import type { GetPersonsInSegmentJsonPerson } from "../../segment/components/manager-v2/types";
import { SupabaseSlackAppService } from "../../slack/server/slack-supabase-service";
import {
  SupabaseFollowUpMomentDetailsService,
  SupabaseFollowUpService,
  type FollowUp,
} from "../server/follow-up-service";
import { isFollowUpWithConfig } from "../utils";

export type FollowUpMomentDetailsConfig =
  | { type: "from_moment"; data: UUID }
  | { type: "follow_up"; data: UUID };

export type FollowUpMomentDetails = Database["public"]["Views"]["follow_up_moment_details"]["Row"];

export type FollowUpSegmentPerson = Omit<GetPersonsInSegmentJsonPerson, "channels"> & {
  channels: Partial<Record<NonNullable<ChannelName>, string>>;
};

export type FollowUpMomentDetailsLoaderData = {
  momentFollowUpDetails: FollowUpMomentDetails;
  enabledChannels: NonNullable<ChannelName>[];
  segmentPersons?: FollowUpSegmentPerson[];
  mergeTags: Tags;
  slackVideoEnabled: boolean;
  followUp?: Omit<FollowUp, "config"> & { config: NonNullable<FollowUp["config"]> };
};

export function useFollowUpMomentDetails(
  config: FollowUpMomentDetailsConfig,
): UseSupabaseLoaderState<FollowUpMomentDetailsLoaderData> {
  return useSupabaseLoader(
    async ({ supabase, account_id }) => {
      const [{ momentFollowUpDetails, followUp }, enabledChannels, mergeTags, slackVideoEnabled] =
        await Promise.all([
          loadMomentAndFollowUp(supabase, account_id, config),
          new SupabaseAccountService(supabase).getEnabledChannels(account_id),
          new SupabaseMergeTagService(supabase).getAllMergeTags(),
          new SupabaseSlackAppService(supabase).videoEnabled(account_id),
        ]);

      if (momentFollowUpDetails.segment?.segment_json) {
        const { data: persons, error: err } = await supabase.rpc("get_persons_in_segment_json", {
          account_id_: account_id,
          segment_json: momentFollowUpDetails.segment.segment_json,
          target_manager_: momentFollowUpDetails.target_manager ?? undefined,
          target_connection_type_: momentFollowUpDetails.target_connection_type ?? undefined,
        });

        if (err) {
          console.error("Error loading persons for Follow-Up segment:", err);
          throw err;
        }

        return {
          momentFollowUpDetails,
          enabledChannels,
          segmentPersons: persons.map((p) => ({
            ...p,
            channels: p.channels as Partial<Record<NonNullable<ChannelName>, string>>,
          })),
          mergeTags,
          slackVideoEnabled,
          followUp,
        };
      }

      return {
        momentFollowUpDetails,
        enabledChannels,
        mergeTags,
        slackVideoEnabled,
        followUp,
      };
    },
    [config],
  );
}

async function loadMomentAndFollowUp(
  supabase: PostgrestClient<DatabaseWithOptions["db"], DatabaseWithOptions["options"], "public">,
  account_id: UUID,
  config: FollowUpMomentDetailsConfig,
): Promise<Pick<FollowUpMomentDetailsLoaderData, "momentFollowUpDetails" | "followUp">> {
  switch (config.type) {
    case "from_moment":
      return loadFromMomentId(supabase, config.data);
    case "follow_up":
      return loadFromFollowUpId(supabase, account_id, config.data);
  }
}

async function loadFromFollowUpId(
  supabase: PostgrestClient<DatabaseWithOptions["db"], DatabaseWithOptions["options"], "public">,
  account_id: UUID,
  follow_up_id: UUID,
): Promise<Pick<FollowUpMomentDetailsLoaderData, "momentFollowUpDetails" | "followUp">> {
  const { data: followUp, error: followUpError } = await new SupabaseFollowUpService(supabase).get(
    follow_up_id,
  );

  if (followUpError) {
    console.error("Error loading Follow-Up:", followUpError);
    throw followUpError;
  }

  if (!isFollowUpWithConfig(followUp)) {
    throw new Error("Follow up does not have a linked Communication");
  }

  const { data: momentFollowUpDetails, error: momentFollowUpDetailsError } =
    await new SupabaseFollowUpMomentDetailsService(supabase).get(followUp.config.source_id as UUID);

  if (momentFollowUpDetailsError) throw momentFollowUpDetailsError;

  return {
    momentFollowUpDetails,
    followUp,
  };
}

async function loadFromMomentId(
  supabase: PostgrestClient<DatabaseWithOptions["db"], DatabaseWithOptions["options"], "public">,
  moment_id: UUID,
): Promise<Pick<FollowUpMomentDetailsLoaderData, "momentFollowUpDetails">> {
  const [{ data: momentFollowUpDetails, error: momentFollowUpDetailsError }] = await Promise.all([
    new SupabaseFollowUpMomentDetailsService(supabase).get(moment_id),
  ]);

  if (momentFollowUpDetailsError) throw momentFollowUpDetailsError;

  return {
    momentFollowUpDetails,
  };
}
